import React from "react";
import styled from "styled-components";

// Extension to color mapping
const extensionColors = {
  PDF: "#D32F2F",
  DOC: "#1976D2",
  DOCX: "#1976D2",
  XLS: "#388E3C",
  XLSX: "#388E3C",
  PPT: "#E64A19",
  PPTX: "#E64A19",
  JPG: "#FBC02D",
  JPEG: "#FBC02D",
  PNG: "#0288D1",
  GIF: "#7B1FA2",
  TXT: "#455A64",
  ZIP: "#F57C00",
  MP3: "#009688",
  MP4: "#FF5722",
};

const defaultColor = "#1976D2";

const ScrollableContainer = styled.div`
  max-height: 150px; /* Set the maximum height of the scrollable area */
  overflow-y: auto; /* Enable vertical scrolling */
`;

// Styled component for the delete button
const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 10px; /* Add some space between download and delete icons */
  display: flex;
  align-items: center;

  svg {
    width: 20px; /* Adjust size as needed */
    height: 20px; /* Adjust size as needed */
    stroke: #757575; /* Default icon color */
    transition: stroke 0.2s ease-in-out;
  }

  &:hover svg {
    stroke: #D32F2F; /* Color change on hover */
  }
`;

// Add loading state for the delete button
const LoadingSpinner = styled.div`
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #757575;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Modified to accept onDeleteFile, isDeleting, and canDelete props
const FileList = ({ files, onDeleteFile, isDeleting, canDelete = true }) => {
  console.log(files);

  // Ensure files is always an array to prevent the .map error
  const filesArray = Array.isArray(files) ? files : [];

  const handleDeleteClick = (file) => {
    if (onDeleteFile && canDelete) {
      // Call the onDeleteFile prop with the file object
      onDeleteFile(file);
    }
  };

  const renderFile = (index, file) => {
    // Determine if this is a local file based on its source.
    const isLocal = !file.source;

    // For local files, use the File object stored in `file.file`.
    const fileName = isLocal
      ? file.blob
        ? file.blob.filename
        : file.file?.name // Use optional chaining for file.name
      : file.name;
    const fileExtension = fileName ? fileName.split(".").pop().toUpperCase() : 'N/A'; // Handle cases where fileName is null/undefined

    // For file size, use file.file.size for local files; otherwise use file.size_bytes or file.size.
    const sizeInBytes = isLocal
      ? file.blob
        ? file.blob.byte_size
        : file.file?.size // Use optional chaining for file.size
      : file.size_bytes || file.size;
    const fileSizeKiB = sizeInBytes !== undefined ? (sizeInBytes / 1024.0).toFixed(1) : 'N/A'; // Handle cases where sizeInBytes is undefined

    // For the file icon:
    // - If it's a file record and an icon is provided, use that.
    // - Otherwise, use a default icon.
    const fileIcon =
      !isLocal && file.icon
        ? file.icon
        : "https://www.dropbox.com/static/images/icons64/page_white.png";

    // For the download URL:
    // For local files, we create a temporary URL from the File object.
    // For file records, use the provided URL.
    const fileURL = isLocal
      ? file.blob
        ? file.url
        : file.file instanceof File ? URL.createObjectURL(file.file) : null // Check if it's a File object before creating URL
      : file.url;

    // Check if the file is the one being deleted
    const isBeingDeleted = isDeleting && file.status === 'deleting';

    return (
      // Use a more robust key if id is not always available, combining multiple properties or index
      <div key={`${file.id || (file.url ? file.url : fileName)}-${index}`}>
        <div className="cdx-attaches cdx-attaches--with-file">
          <div className="cdx-attaches__file-icon">
            {!isLocal && file.icon ? (
              <img
                src={fileIcon}
                alt={`${fileExtension} icon`}
                className="cdx-attaches__file-icon-background"
              />
            ) : (
              <>
                <div
                  className="cdx-attaches__file-icon-background"
                  style={{
                    backgroundColor:
                      extensionColors[fileExtension] || defaultColor,
                  }}
                ></div>
                <div
                  className="cdx-attaches__file-icon-label"
                  title={fileExtension}
                  style={{
                    backgroundColor:
                      extensionColors[fileExtension] || defaultColor,
                  }}
                >
                  {fileExtension}
                </div>
              </>
            )}
          </div>
          <div className="cdx-attaches__file-info">
            <div
              className="cdx-attaches__title"
              data-placeholder="File title"
              data-empty="false"
            >
              {fileName || 'Unknown File'} {/* Display a fallback if fileName is null/undefined */}
            </div>
            <div className="cdx-attaches__size">{fileSizeKiB} KiB</div>
          </div>
          {fileURL && (
            <a
              className="cdx-attaches__download-button"
              href={fileURL}
              target="_blank"
              rel="nofollow noindex noreferrer"
              // Revoke the object URL if it was created for a local file after download
              onClick={() => {
                if (isLocal && file.file instanceof File) {
                  // A small delay might be needed to allow download to start
                  // Also consider if revoking immediately is appropriate or if you need to keep it for display
                  // For a list that persists, you might not revoke until deletion or component unmount
                  // setTimeout(() => URL.revokeObjectURL(fileURL), 100);
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="2"
                  d="M7 10L11.8586 14.8586C11.9367 14.9367 12.0633 14.9367 12.1414 14.8586L17 10"
                ></path>
              </svg>
            </a>
          )}

          {/* Delete Button - Only show if canDelete is true */}
          {canDelete && (
            <DeleteButton 
              onClick={() => handleDeleteClick(file)} 
              disabled={isDeleting}
              title="Delete file"
            >
              {isBeingDeleted ? (
                <LoadingSpinner />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor" // Set stroke to currentColor to use the button's text color
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                </svg>
              )}
            </DeleteButton>
          )}
        </div>
      </div>
    );
  };

  return (
    <ScrollableContainer>
      {/* Map over the filesArray, which is guaranteed to be an array */}
      {filesArray.map((file, i) => renderFile(i, file))}
    </ScrollableContainer>
  );
};

export default FileList;
